import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Seo } from "@/components/web/Seo";
import { FadeUp, MaskedLines } from "@/components/web/Reveal";
import { AdSlot } from "@/components/web/AdSlot";
import { fetchPosts, fetchCategories } from "@/lib/blogApi";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "";

export default function Blog() {
  const [posts, setPosts] = useState(null);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");

  useEffect(() => {
    fetchCategories().then(setCats).catch(() => {});
  }, []);
  useEffect(() => {
    setPosts(null);
    fetchPosts(cat).then(setPosts).catch(() => setPosts([]));
  }, [cat]);

  return (
    <>
      <Seo
        title="Blog — LinkExpln"
        description="Practical link-safety guides from LinkExpln: how to spot phishing, scam and malicious links, and how to stay safe online."
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://linkexpln.com/" },
            { "@type": "ListItem", position: 2, name: "Blog", item: "https://linkexpln.com/blog" },
          ],
        })}</script>
      </Helmet>

      <section className="pt-24 pb-16 sm:pt-32 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <nav aria-label="Breadcrumb" data-testid="blog-breadcrumb" className="mb-6 font-mono-ls text-[11px] tracking-[0.15em] uppercase text-zinc-400">
            <Link to="/" className="hover:text-[#00C5FE] transition-colors duration-200">Home</Link>
            <span className="mx-2 text-zinc-600">/</span>
            <span className="text-[#00C5FE]">Blog</span>
          </nav>
          <h1 data-testid="blog-heading" className="font-heading font-black tracking-tighter text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
            <MaskedLines lines={["THE LINK", "SAFETY LOG."]} />
          </h1>
          <p className="mt-8 max-w-xl text-zinc-400 text-base sm:text-lg leading-relaxed">
            Practical guides on spotting phishing, scams and malicious links — from the team behind the{" "}
            <Link to="/scanner" className="text-[#00C5FE] hover:text-white underline underline-offset-4 transition-colors duration-200">LinkExpln scanner</Link>.
          </p>
          {cats.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2">
              <button data-testid="blog-cat-all" onClick={() => setCat("")}
                className={`px-4 py-2 font-mono-ls text-[11px] tracking-[0.12em] uppercase border transition-colors duration-200 ${!cat ? "btn-brand text-black border-transparent" : "border-white/15 text-zinc-400 hover:text-white"}`}>
                All
              </button>
              {cats.map((c) => (
                <button key={c} data-testid={`blog-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setCat(c)}
                  className={`px-4 py-2 font-mono-ls text-[11px] tracking-[0.12em] uppercase border transition-colors duration-200 ${cat === c ? "btn-brand text-black border-transparent" : "border-white/15 text-zinc-400 hover:text-white"}`}>
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          {posts === null && <p className="font-mono-ls text-xs tracking-[0.2em] uppercase text-zinc-400">Loading articles…</p>}
          {posts && posts.length === 0 && (
            <p data-testid="blog-empty" className="text-zinc-400">No articles published yet — check back soon.</p>
          )}
          {posts && posts.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {posts.map((p, i) => (
                <FadeUp key={p.id} delay={(i % 3) * 0.06} className="bg-[#0A121D] hover:bg-[#101A28] transition-colors duration-300 flex flex-col">
                  <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="flex flex-col h-full p-8">
                    {p.featured_image ? (
                      <img src={p.featured_image} alt={p.title} loading="lazy" className="w-full h-40 object-cover mb-6 border border-white/10" />
                    ) : (
                      <div className="w-full h-40 mb-6 border border-white/10 bg-black grid place-items-center">
                        <span className="font-heading font-black text-2xl text-white/10">LINKEXPLN</span>
                      </div>
                    )}
                    <p className="font-mono-ls text-[10px] tracking-[0.2em] uppercase text-[#00C5FE] mb-3">{p.category}</p>
                    <h2 className="font-heading font-bold text-lg text-white leading-snug">{p.title}</h2>
                    <p className="mt-3 text-zinc-400 text-sm leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>
                    <div className="mt-5 flex items-center justify-between">
                      <span className="font-mono-ls text-[10px] tracking-[0.15em] text-zinc-400">{fmtDate(p.published_at)} · {p.author}</span>
                      <span className="font-heading font-semibold text-xs text-[#00C5FE]">Read more →</span>
                    </div>
                  </Link>
                </FadeUp>
              ))}
            </div>
          )}
          {posts && posts.length > 0 && <AdSlot id="blog-listing" className="mt-12" />}
        </div>
      </section>
    </>
  );
}
